/* Shown after a point lands on a day, whether by drag or by "Add here". It asks how the stop
   recurs, which is the one thing the gesture cannot express:
     every → the base weekly series for that weekday
     nth   → only the Nth occurrence of that weekday each month
     date  → only that exact calendar date (one-off)

   antd `<Modal>` per docs/design/spec/02-components.md, which also brings the mask, the focus trap,
   focus return and Escape — the hand-rolled backdrop this replaced had none of them. */

import React from "react";
import { Button, Flex, Modal, Typography } from "antd";
import { CalendarOutlined, PushpinOutlined, RetweetOutlined, SyncOutlined } from "@ant-design/icons";

import { ROUTE_CALENDAR } from "../../../config/lang";
import { fill, translate } from "../../../util/util";
import { dayMonthLabel, localeFor, nthOfMonth, weekdayMon0 } from "./dateUtils";
import { biweeklyNths } from "./routeModel";
import { ukBiweekly, ukEvery, ukNth } from "./ukGrammar";

const ORDINAL_KEY = { 1: "ord_first", 2: "ord_second", 3: "ord_third", 4: "ord_fourth", 5: "ord_fifth" };

export default function ScopeMenu({ date, point, lang, onChoose, onCancel }) {
  const t = key => translate(ROUTE_CALENDAR, key, lang);
  const wd = weekdayMon0(date);
  const localeWeekday = new Intl.DateTimeFormat(localeFor(lang), { weekday: "long" }).format(date);
  const nth = nthOfMonth(date);

  /* Ukrainian needs genitive + gender agreement, which a flat template cannot express; those two
     phrases are built explicitly. Other languages use the {day}/{ord} template. */
  const everyLabel = lang === "uk" ? ukEvery(wd) : fill(t("scope_every"), { day: localeWeekday });
  const nthLabel = lang === "uk" ? ukNth(wd, nth) : fill(t("scope_nth"), { ord: t(ORDINAL_KEY[nth]), day: localeWeekday });

  /* "Once every two weeks" = the 1st+3rd or 2nd+4th slot pair (routeModel.biweeklyNths); a 5th
     occurrence pairs with nothing, so the option simply isn't offered there. */
  const pair = biweeklyNths(date);
  const biweeklyLabel = pair
    ? lang === "uk"
      ? ukBiweekly(wd, pair)
      : fill(t("scope_biweekly"), { ordA: t(ORDINAL_KEY[pair[0]]), ordB: t(ORDINAL_KEY[pair[1]]), day: localeWeekday })
    : null;

  const options = [
    { type: "every", label: everyLabel, icon: <RetweetOutlined /> },
    { type: "nth", label: nthLabel, icon: <CalendarOutlined /> },
    ...(biweeklyLabel ? [{ type: "biweekly", label: biweeklyLabel, icon: <SyncOutlined /> }] : []),
    { type: "date", label: fill(t("scope_date"), { date: dayMonthLabel(date, lang) }), icon: <PushpinOutlined /> },
  ];

  return (
    <Modal
      open
      rootClassName="rc-scope"
      title={t("scope_title")}
      /* The three options ARE the actions; a footer of OK/Cancel would leave the primary action
         ambiguous. Cancel stays available as the modal's own close and Escape. */
      footer={null}
      width={440}
      centered
      onCancel={onCancel}>
      <Typography.Text type="secondary" className="rc-scope__point">
        {point?.name}
      </Typography.Text>
      <Flex vertical gap="small" className="rc-scope__opts">
        {options.map(option => (
          <Button key={option.type} className="rc-scope__opt" size="large" block icon={option.icon} onClick={() => onChoose(option.type)}>
            {option.label}
          </Button>
        ))}
      </Flex>
    </Modal>
  );
}
